import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import {
  AgentChatHistoryCompressionType,
  AgentReasoningEffort,
  AgentReasoningSummary,
  AgentRuntime,
  AgentSettingsInput,
  ASRProviderReferenceSuiteOutput,
  DataInterchange,
  DataInterchangeFormat,
  DataInterfaceType,
  LLMApiType,
  LLMSettings,
  OCRProviderReferenceSuiteOutput,
  ProviderModel,
} from '../../../gamma-api';
import IbTypography from '../common/IbTypography';
import IbSelect from '../common/IbSelect';
import ModelSelector from '../ModelSelector';
import IbScrollSpy from '../common/IbScrollSpy';
import IbButton from '../common/IbButton';
import Switcher from '../common/Switcher';
import IbModal, { IbModalPosition } from '../common/IbModal';
import IbTabs from '../common/IbTabs';
import IbTabPane from '../common/IbTabPane';
import IbSpin from '../common/IbSpin';
import IbIcon from '../common/IbIcon';
import SchemaEditorExpandable from '../common/SchemaEditorExpandable';
import { MONACO_FULL_EDITOR_OPTIONS } from '../common/CodeEditorExpandable/const';
import IbTooltip from '../common/IbTooltip';
import { CodeEditorStatus } from '../common/CodeEditor/types';
import { DEFAULT_EDITOR_STATUS } from '../common/CodeEditor/const';
import IbNumberInput from '../common/IbNumberInput';
import { tryParseIntValue } from '../../utils/numberUtil';
import IbSwitch from '../common/IbSwitch';
import PromptEditorExpandable from '../common/PromptEditorExpandable';
import { PromptLanguage } from '../common/PromptEditor/const';
import { CodeEditorSize } from '../common/CodeEditorExpandable/types';
import { getJsonMetaSchema, schemaGetPropertyNamesWithSpaces } from '../common/SchemaEditor/utils';
import { getDefaultIfUndefined } from '../../utils/typeUtil';
import {
  AGENT_CHAT_HISTORY_SUMMARIZATION_INSTRUCTION_DEFAULT,
  AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_DEFAULT,
  AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_MIN,
  AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_DEFAULT,
  AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MAX,
  AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MIN,
  AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_DEFAULT,
  AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MAX,
  AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MIN,
  AGENT_EXTRA_MAX_TURNS_MIN_VALUE,
  AGENT_EXTRA_TOOL_CALL_OUTPUT_MAX_SIZE_MIN_VALUE,
} from '../../constants/agents';
import RecognizerSelector from '../RecognizerSelector';

import {
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  CONTENT_CLASS_NAME,
  FOOTER_CLASS_NAME,
  MAIN_CLASS_NAME,
  SCHEMA_FRAME_CLASS_NAME,
  SCHEMA_FRAME_LOADING_CLASS_NAME,
  SCHEMA_FRAME_SPIN_CLASS_NAME,
  SCHEMA_MODAL_CLASS_NAME,
  SWITCH_DESCRIPTION_CLASS_NAME,
  TOOL_CALL_OUTPUT_MAX_SIZE_CLASS_NAME,
} from './const';
import { getCodeFolder } from './utils';

const PAGE_MESSAGE_EVENT_TYPE = 'message';
const DATA_SCHEMA_CHANGE_MESSAGE_TYPE = 'schema.data.change';
const META_SCHEMA_CHANGE_MESSAGE_TYPE = 'schema.meta.change';

const USE_SCHEMA_MONACO_EDITOR = false;
const USE_SCHEMA_JSON_BUILDER = true;

const OBJECT_JSON_SCHEMA = {
  title: 'JSON object',
  type: 'object',
  properties: {},
  additionalProperties: true,
};

export const DEFAULT_AGENT_SETTINGS = {
  llm: {},
  runtime: AgentRuntime.OpenAi,
  interface: {
    type: DataInterfaceType.Chat,
    input: {
      data_format: DataInterchangeFormat.Text,
      json_schema: null,
    },
    output: {
      data_format: DataInterchangeFormat.Text,
      json_schema: null,
    },
  },
  widget: {
    avatar: {
      name: 'default',
    },
    display_name: '',
    suggestions: [],
  },
  chat_history: {
    compression_type: AgentChatHistoryCompressionType.Disabled,
    summarization: {},
  },
  extra: {
    llm_api_type: LLMApiType.ChatCompletions,
    streaming: false,
    parallel_tool_calls: false,
    max_turns: 10,
    tool_call_output_max_size: 50000,
    use_final_answer_tool: false,
    reasoning: {
      enabled: false,
      effort: AgentReasoningEffort.Medium,
      summary: AgentReasoningSummary.Auto,
    },
  },
} as AgentSettingsInput;

enum SchemeControlTabKey {
  MonacoEditor = 'monaco-editor',
  JsonBuilder = 'json-builder',
}

export interface IAgentGeneralProps {
  interfaceType?: DataInterfaceType;
  agentId?: string;
  agentSettings?: AgentSettingsInput;
  providers: ProviderModel[];
  providersLoading: boolean;
  onSave: (settings: AgentSettingsInput) => Promise<void>;
}

const formatSchemaText = (value?: { [key: string]: unknown } | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  return JSON.stringify(value, undefined, 2);
};

const parseSchemaJson = (value?: string | null): { [key: string]: unknown } | undefined => {
  if (!value) {
    return undefined;
  }

  return JSON.parse(value);
};

const getSchemaPropertyNamesWithSpaces = (content?: string): string[] => {
  if (!content) return [];
  try {
    return schemaGetPropertyNamesWithSpaces(parseSchemaJson(content));
  } catch {
    return [];
  }
};

const hasSchemaPropertyNamesWithSpaces = (content?: string): boolean =>
  getSchemaPropertyNamesWithSpaces(content).length > 0;

const AgentGeneral: React.FC<IAgentGeneralProps> = ({
  interfaceType,
  agentId,
  agentSettings,
  providers,
  providersLoading,
  onSave,
}) => {
  const { t, i18n } = useTranslation();

  const schemaBuilderRef = useRef(null);

  const [settings, setSettings] = useState(agentSettings || DEFAULT_AGENT_SETTINGS);
  const [saving, setSaving] = useState(false);

  const [inputSchemaContent, setInputSchemaContent] = useState<string>();
  const [inputSchemaStatus, setInputSchemaStatus] = useState<CodeEditorStatus>(DEFAULT_EDITOR_STATUS);

  const [outputSchemaContent, setOutputSchemaContent] = useState<string>();
  const [outputSchemaStatus, setOutputSchemaStatus] = useState<CodeEditorStatus>(DEFAULT_EDITOR_STATUS);

  const [schemaModalContent, setSchemaModalContent] = useState<string>();
  const schemaModalSpaceNames = useMemo(() => getSchemaPropertyNamesWithSpaces(schemaModalContent), [
    schemaModalContent,
  ]);
  const [schemaEditorStatus, setSchemaEditorStatus] = useState<CodeEditorStatus>(DEFAULT_EDITOR_STATUS);
  const [schemaBuilderLoading, setSchemaBuilderLoading] = useState<boolean>(false);
  const [schemaControlTabKey, setSchemaControlTabKey] = useState(SchemeControlTabKey.MonacoEditor);
  const [schemaModalInterchange, setSchemaModalInterchange] = useState<DataInterchange>();
  const [schemaModalVisible, setSchemaModalVisible] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const message = event.data;
      if (!message) {
        return;
      }

      if (message.type !== DATA_SCHEMA_CHANGE_MESSAGE_TYPE) {
        return;
      }

      const content = formatSchemaText(message.data);
      setSchemaModalContent(content);
    };

    window.addEventListener(PAGE_MESSAGE_EVENT_TYPE, handleMessage);
    return () => {
      window.removeEventListener(PAGE_MESSAGE_EVENT_TYPE, handleMessage);
    };
  }, []);

  const setSchemaBuilderData = (value?: string) => {
    const frame = schemaBuilderRef.current;
    if (!frame) {
      return;
    }

    let schema;
    try {
      schema = parseSchemaJson(value);
    } catch {
      setSchemaEditorStatus(CodeEditorStatus.Error);
      return;
    }

    const message = {
      type: DATA_SCHEMA_CHANGE_MESSAGE_TYPE,
      data: schema || {},
    };
    (frame as { contentWindow: Window }).contentWindow.postMessage(message, '*');
  };

  const setSchemaBuilderMeta = () => {
    const frame = schemaBuilderRef.current;
    if (!frame) {
      return;
    }

    const schema = getJsonMetaSchema(i18n);

    const message = {
      type: META_SCHEMA_CHANGE_MESSAGE_TYPE,
      data: schema,
    };
    (frame as { contentWindow: Window }).contentWindow.postMessage(message, '*');
  };

  const clearSchemaModalState = () => {
    setSchemaModalContent(undefined);
    setSchemaEditorStatus(DEFAULT_EDITOR_STATUS);
    setSchemaBuilderLoading(false);
    setSchemaModalInterchange(undefined);
    setSchemaModalVisible(false);
  };

  const onInterfaceTypeChange = (value: SelectValue) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          interface: {
            ...prev.interface,
            type: value,
          },
        } as AgentSettingsInput)
    );
  };

  const onInputFormatChange = (value: string | boolean) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          interface: {
            ...prev.interface,
            input: {
              ...prev.interface.input,
              data_format: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onOutputFormatChange = (value: string | boolean) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          interface: {
            ...prev.interface,
            output: {
              ...prev.interface.output,
              data_format: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onInputSchemaChange = (value?: string) => {
    setInputSchemaContent(value);

    let schema: unknown;
    try {
      schema = parseSchemaJson(value);
    } catch {
      setInputSchemaStatus(CodeEditorStatus.Error);
      return;
    }

    setSettings(
      (prev) =>
        ({
          ...prev,
          interface: {
            ...prev.interface,
            input: {
              ...prev.interface.input,
              json_schema: schema,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onOutputSchemaChange = (value?: string) => {
    setOutputSchemaContent(value);

    let schema: unknown;
    try {
      schema = parseSchemaJson(value);
    } catch {
      setOutputSchemaStatus(CodeEditorStatus.Error);
      return;
    }

    setSettings(
      (prev) =>
        ({
          ...prev,
          interface: {
            ...prev.interface,
            output: {
              ...prev.interface.output,
              json_schema: schema,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onInputSchemaValidate = (errors: string[], status: CodeEditorStatus) => {
    setInputSchemaStatus(status);
  };

  const onOutputSchemaValidate = (errors: string[], status: CodeEditorStatus) => {
    setOutputSchemaStatus(status);
  };

  const onLLMChange = (llm: LLMSettings) => {
    setSettings((prev) => ({ ...prev, llm } as AgentSettingsInput));
  };

  const onOcrChange = (ocr: OCRProviderReferenceSuiteOutput) => {
    setSettings((prev) => ({ ...prev, ocr } as AgentSettingsInput));
  };

  const onAsrChange = (asr: ASRProviderReferenceSuiteOutput) => {
    setSettings((prev) => ({ ...prev, asr } as AgentSettingsInput));
  };

  const onRuntimeTypeChange = (value: SelectValue) => {
    setSettings((prev) => ({ ...prev, runtime: value as AgentRuntime } as AgentSettingsInput));
  };

  const onInstructionsChange = (value: string) => {
    setSettings((prev) => ({ ...prev, instructions: value } as AgentSettingsInput));
  };

  const onChatHistoryCompressionTypeChange = (value: SelectValue) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            compression_type: value as AgentChatHistoryCompressionType,
          },
        } as AgentSettingsInput)
    );
  };

  const onChatHistorySummarizationLLMChange = (llm: LLMSettings) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            summarization: {
              ...prev.chat_history.summarization,
              llm,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onChatHistorySummarizationInstructionsChange = (value: string) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            summarization: {
              ...prev.chat_history.summarization,
              instructions: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onChatHistorySummarizationThresholdChange = (value: string | number) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            summarization: {
              ...prev.chat_history.summarization,
              threshold: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onChatHistorySummarizationTargetRatioChange = (value: string | number) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            summarization: {
              ...prev.chat_history.summarization,
              target_ratio: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onChatHistorySummarizationProtectLastNChange = (value: string | number) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          chat_history: {
            ...prev.chat_history,
            summarization: {
              ...prev.chat_history.summarization,
              protect_last_n: value,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onExtraLLMApiTypeChange = (value: SelectValue) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: { ...prev.extra, llm_api_type: value as LLMApiType },
        } as AgentSettingsInput)
    );
  };

  const onExtraMaxTurnsChange = (value: string | number) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: { ...prev.extra, max_turns: tryParseIntValue(value) ?? AGENT_EXTRA_MAX_TURNS_MIN_VALUE },
        } as AgentSettingsInput)
    );
  };

  const onExtraToolCallOutputMaxSizeChange = (value: string | number) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: {
            ...prev.extra,
            tool_call_output_max_size: tryParseIntValue(value) ?? AGENT_EXTRA_TOOL_CALL_OUTPUT_MAX_SIZE_MIN_VALUE,
          },
        } as AgentSettingsInput)
    );
  };

  const onExtraParallelToolCallsChange = () => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: { ...prev.extra, parallel_tool_calls: !prev.extra.parallel_tool_calls },
        } as AgentSettingsInput)
    );
  };

  const onExtraUseFinalAnswerToolChange = () => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: { ...prev.extra, use_final_answer_tool: !prev.extra.use_final_answer_tool },
        } as AgentSettingsInput)
    );
  };

  const onExtraStreamingChange = () => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: { ...prev.extra, streaming: !prev.extra.streaming },
        } as AgentSettingsInput)
    );
  };

  const onExtraReasoningEnabledChange = () => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: {
            ...prev.extra,
            reasoning: {
              ...prev.extra.reasoning,
              enabled: !prev.extra.reasoning.enabled,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onExtraReasoningEffortChange = (value: SelectValue) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: {
            ...prev.extra,
            reasoning: {
              ...prev.extra.reasoning,
              effort: value as AgentReasoningEffort,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onExtraReasoningSummaryChange = (value: SelectValue) => {
    setSettings(
      (prev) =>
        ({
          ...prev,
          extra: {
            ...prev.extra,
            reasoning: {
              ...prev.extra.reasoning,
              summary: value as AgentReasoningSummary,
            },
          },
        } as AgentSettingsInput)
    );
  };

  const onSaveButtonClick = async () => {
    setSaving(true);
    await onSave(settings);
    setSaving(false);
  };

  const onAgentSettingsPropChange = () => {
    setSettings(agentSettings || DEFAULT_AGENT_SETTINGS);

    if (!agentSettings) {
      return;
    }

    const inputSchema = agentSettings.interface.input.json_schema || OBJECT_JSON_SCHEMA;
    const outputSchema = agentSettings.interface.output.json_schema || OBJECT_JSON_SCHEMA;
    const inputContent = formatSchemaText(inputSchema);
    const outputContent = formatSchemaText(outputSchema);

    setInputSchemaContent(inputContent);
    setOutputSchemaContent(outputContent);
  };
  useEffect(onAgentSettingsPropChange, [agentSettings]);

  const onSchemaModalShow = (interchange: DataInterchange) => {
    if (interchange === settings.interface.input) {
      setSchemaModalContent(inputSchemaContent);
      setSchemaEditorStatus(inputSchemaStatus);
    } else if (interchange === settings.interface.output) {
      setSchemaModalContent(outputSchemaContent);
      setSchemaEditorStatus(outputSchemaStatus);
    }
    setSchemaBuilderLoading(USE_SCHEMA_JSON_BUILDER);
    setSchemaModalInterchange(interchange);
    setSchemaModalVisible(true);
  };

  const onSchemaModalSave = () => {
    if (schemaModalInterchange === settings.interface.input) {
      onInputSchemaChange(schemaModalContent);
      setInputSchemaStatus(schemaEditorStatus);
    } else if (schemaModalInterchange === settings.interface.output) {
      onOutputSchemaChange(schemaModalContent);
      setOutputSchemaStatus(schemaEditorStatus);
    }
    clearSchemaModalState();
  };

  const onSchemaModalHide = () => {
    clearSchemaModalState();
  };

  const onInputSchemaModalShow = () => {
    onSchemaModalShow(settings.interface.input);
  };

  const onOutputSchemaModalShow = () => {
    onSchemaModalShow(settings.interface.output);
  };

  const onSchemaControlTabChange = (key: string) => setSchemaControlTabKey(key as SchemeControlTabKey);

  const onMonacoEditorChange = (value?: string) => {
    setSchemaModalContent(value);
    setSchemaBuilderData(value);
  };

  const onMonacoEditorValidate = (errors: string[], status: CodeEditorStatus) => {
    setSchemaEditorStatus(status);
  };

  const onSchemaBuilderLoaded = () => {
    setSchemaBuilderData(schemaModalContent);
    setSchemaBuilderMeta();
    setSchemaBuilderLoading(false);
  };

  const inputFormatOptions = [
    {
      value: DataInterchangeFormat.Text,
      label: t('Agent: Settings: Interchange: Input: Text: Title'),
      tooltip: t('Agent: Settings: Interchange: Input: Text: Tooltip'),
    },
    {
      value: DataInterchangeFormat.Json,
      label: t('Agent: Settings: Interchange: Input: JSON: Title'),
      tooltip: t('Agent: Settings: Interchange: Input: JSON: Tooltip'),
    },
    {
      value: DataInterchangeFormat.Schema,
      label: t('Agent: Settings: Interchange: Input: Schema: Title'),
      tooltip: t('Agent: Settings: Interchange: Input: Schema: Tooltip'),
    },
  ];

  const outputFormatOptions = [
    {
      value: DataInterchangeFormat.Text,
      label: t('Agent: Settings: Interchange: Output: Text: Title'),
      tooltip: t('Agent: Settings: Interchange: Output: Text: Tooltip'),
    },
    {
      value: DataInterchangeFormat.Json,
      label: t('Agent: Settings: Interchange: Output: JSON: Title'),
      tooltip: t('Agent: Settings: Interchange: Output: JSON: Tooltip'),
    },
    {
      value: DataInterchangeFormat.Schema,
      label: t('Agent: Settings: Interchange: Output: Schema: Title'),
      tooltip: t('Agent: Settings: Interchange: Output: Schema: Tooltip'),
    },
  ];

  const renderInterfaceBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: Interface: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Interface: Type: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Settings: Interface: Type: Description')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            { value: DataInterfaceType.Chat, label: 'Chat Agent' },
            { value: DataInterfaceType.Api, label: 'API Agent' },
          ]}
          value={settings.interface.type}
          onChange={onInterfaceTypeChange}
        />
      </div>
    </div>
  );

  const renderInterchangeBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: Interchange: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Interchange: Input: Title')}
        </IbTypography.Paragraph>
        <Switcher
          options={inputFormatOptions}
          value={settings.interface.input.data_format}
          onChange={onInputFormatChange}
        />
        <IbTypography className={SWITCH_DESCRIPTION_CLASS_NAME}>
          {settings.interface.input.data_format == DataInterchangeFormat.Text && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Input: Text: Tooltip')}
            </IbTypography.Paragraph>
          )}
          {settings.interface.input.data_format == DataInterchangeFormat.Json && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Input: JSON: Tooltip')}
            </IbTypography.Paragraph>
          )}
          {settings.interface.input.data_format == DataInterchangeFormat.Schema && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Input: Schema: Tooltip')}
            </IbTypography.Paragraph>
          )}
        </IbTypography>
        {settings.interface.input.data_format === DataInterchangeFormat.Schema && (
          <>
            <SchemaEditorExpandable
              codeFolder={getCodeFolder(agentId, 'input')}
              status={inputSchemaStatus}
              value={inputSchemaContent}
              onChange={onInputSchemaChange}
              onValidate={onInputSchemaValidate}
            />
            <IbButton icon={<IbIcon iconName="setting-two" />} type="link" onClick={onInputSchemaModalShow}>
              {t('Agent: Settings: Interchange: Schema: Button: Title')}
            </IbButton>
          </>
        )}
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Interchange: Output: Title')}
        </IbTypography.Paragraph>
        <Switcher
          options={outputFormatOptions}
          value={settings.interface.output.data_format}
          onChange={onOutputFormatChange}
        />
        <IbTypography className={SWITCH_DESCRIPTION_CLASS_NAME}>
          {settings.interface.output.data_format == DataInterchangeFormat.Text && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Output: Text: Tooltip')}
            </IbTypography.Paragraph>
          )}
          {settings.interface.output.data_format == DataInterchangeFormat.Json && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Output: JSON: Tooltip')}
            </IbTypography.Paragraph>
          )}
          {settings.interface.output.data_format == DataInterchangeFormat.Schema && (
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Interchange: Output: Schema: Tooltip')}
            </IbTypography.Paragraph>
          )}
        </IbTypography>
        {settings.interface.output.data_format === DataInterchangeFormat.Schema && (
          <>
            <SchemaEditorExpandable
              codeFolder={getCodeFolder(agentId, 'output')}
              status={outputSchemaStatus}
              value={outputSchemaContent}
              onChange={onOutputSchemaChange}
              onValidate={onOutputSchemaValidate}
            />
            <IbButton icon={<IbIcon iconName="setting-two" />} type="link" onClick={onOutputSchemaModalShow}>
              {t('Agent: Settings: Interchange: Schema: Button: Title')}
            </IbButton>
          </>
        )}
      </div>
    </div>
  );

  const renderResponseGenerationBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: Response generation: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Response generation: Instructions: Title')}
        </IbTypography.Paragraph>
        <PromptEditorExpandable
          promptLanguage={PromptLanguage.PLAIN_TEXT}
          size={CodeEditorSize.XL}
          value={settings.instructions}
          onChange={onInstructionsChange}
        />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Response generation: LLM: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Settings: Response generation: LLM: Description')}
        </IbTypography.Paragraph>
        <ModelSelector.LLM
          loading={providersLoading}
          providers={providers}
          settings={settings.llm}
          onChange={onLLMChange}
        />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Response generation: Runtime type: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            { value: AgentRuntime.OpenAi, label: 'OpenAI' },
            { value: AgentRuntime.ReAct, label: 'ReAct' },
          ]}
          value={settings.runtime}
          onChange={onRuntimeTypeChange}
        />
      </div>
    </div>
  );

  const renderOcrBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: OCR: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <RecognizerSelector.Ocr suite={settings.ocr} onChange={onOcrChange} />
      </div>
    </div>
  );

  const renderAsrBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: ASR: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <RecognizerSelector.Asr suite={settings.asr} onChange={onAsrChange} />
      </div>
    </div>
  );

  const renderChatHistoryBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: Chat history: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Chat history: Compression type: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            {
              value: AgentChatHistoryCompressionType.Disabled,
              label: t('Agent: Settings: Chat history: Compression type: Disabled: Title'),
            },
            {
              value: AgentChatHistoryCompressionType.Summarization,
              label: t('Agent: Settings: Chat history: Compression type: Summarization: Title'),
            },
          ]}
          value={settings.chat_history.compression_type}
          onChange={onChatHistoryCompressionTypeChange}
        />
      </div>
      {settings.chat_history.compression_type == AgentChatHistoryCompressionType.Summarization && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Settings: Chat history: Summarization: LLM: Title')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Chat history: Summarization: LLM: Description')}
            </IbTypography.Paragraph>
            <ModelSelector.LLM
              loading={providersLoading}
              providers={providers}
              settings={settings.chat_history.summarization.llm}
              onChange={onChatHistorySummarizationLLMChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Instructions: Title')}
            </IbTypography.Paragraph>
            <PromptEditorExpandable
              promptLanguage={PromptLanguage.CHAT_HISTORY_SUMMARIZATION}
              size={CodeEditorSize.XL}
              value={getDefaultIfUndefined(
                settings.chat_history.summarization.instructions,
                AGENT_CHAT_HISTORY_SUMMARIZATION_INSTRUCTION_DEFAULT
              )}
              onChange={onChatHistorySummarizationInstructionsChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Threshold: Title')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Threshold: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              defaultValue={AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_DEFAULT}
              max={AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MAX}
              min={AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MIN}
              precision={0}
              value={settings.chat_history.summarization.threshold || undefined}
              onChange={onChatHistorySummarizationThresholdChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Target ratio: Title')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Target ratio: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              defaultValue={AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_DEFAULT}
              max={AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MAX}
              min={AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MIN}
              precision={0}
              value={settings.chat_history.summarization.target_ratio || undefined}
              onChange={onChatHistorySummarizationTargetRatioChange}
            />
            <IbTypography.Paragraph type="secondary">
              {t('Agent: Settings: Chat history: Summarization: Protect last n: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              defaultValue={AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_DEFAULT}
              min={AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_MIN}
              precision={0}
              value={settings.chat_history.summarization.protect_last_n || undefined}
              onChange={onChatHistorySummarizationProtectLastNChange}
            />
          </div>
        </>
      )}
    </div>
  );

  const renderExtraParametersBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Settings: Extra parameters: Title')}</h4>
      </IbTypography>
      {settings.runtime == AgentRuntime.OpenAi && (
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Settings: Extra parameters: API type: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={[
              { value: LLMApiType.ChatCompletions, label: 'Chat Completions API' },
              { value: LLMApiType.Responses, label: 'Responses API' },
            ]}
            value={settings.extra.llm_api_type}
            onChange={onExtraLLMApiTypeChange}
          />
        </div>
      )}
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Extra parameters: Max turns: Title')}
        </IbTypography.Paragraph>
        <IbNumberInput
          min={AGENT_EXTRA_MAX_TURNS_MIN_VALUE}
          precision={0}
          value={settings.extra.max_turns}
          onChange={onExtraMaxTurnsChange}
        />
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Settings: Extra parameters: Tool call output max size: Title')}
        </IbTypography.Paragraph>
        <IbNumberInput
          className={TOOL_CALL_OUTPUT_MAX_SIZE_CLASS_NAME}
          min={AGENT_EXTRA_TOOL_CALL_OUTPUT_MAX_SIZE_MIN_VALUE}
          precision={0}
          value={settings.extra.tool_call_output_max_size}
          onChange={onExtraToolCallOutputMaxSizeChange}
        />
      </div>
      {settings.runtime == AgentRuntime.OpenAi && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={settings.extra.parallel_tool_calls}
                size="small"
                onChange={onExtraParallelToolCallsChange}
              />
              <IbTypography.Paragraph strong>
                {t('Agent: Settings: Extra parameters: Parallel tool calls: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={settings.extra.use_final_answer_tool || undefined}
                size="small"
                onChange={onExtraUseFinalAnswerToolChange}
              />
              <IbTypography.Paragraph strong>
                {t('Agent: Settings: Extra parameters: Use final answer tool: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
          </div>
        </>
      )}
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={settings.extra.streaming} size="small" onChange={onExtraStreamingChange} />
          <IbTypography.Paragraph strong>
            {t('Agent: Settings: Extra parameters: Streaming: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
      </div>
      {settings.runtime == AgentRuntime.OpenAi && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={settings.extra.reasoning.enabled}
                size="small"
                onChange={onExtraReasoningEnabledChange}
              />
              <IbTypography.Paragraph strong>
                {t('Agent: Settings: Extra parameters: Reasoning: Enabled: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
          </div>
          {settings.extra.reasoning.enabled && (
            <>
              <div className={BLOCK_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {t('Agent: Settings: Extra parameters: Reasoning: Effort: Title')}
                </IbTypography.Paragraph>
                <IbSelect
                  options={[
                    { value: AgentReasoningEffort.None, label: AgentReasoningEffort.None.toLowerCase() },
                    { value: AgentReasoningEffort.Minimal, label: AgentReasoningEffort.Minimal.toLowerCase() },
                    { value: AgentReasoningEffort.Low, label: AgentReasoningEffort.Low.toLowerCase() },
                    { value: AgentReasoningEffort.Medium, label: AgentReasoningEffort.Medium.toLowerCase() },
                    { value: AgentReasoningEffort.High, label: AgentReasoningEffort.High.toLowerCase() },
                    { value: AgentReasoningEffort.Xhigh, label: AgentReasoningEffort.Xhigh.toLowerCase() },
                  ]}
                  value={settings.extra.reasoning.effort}
                  onChange={onExtraReasoningEffortChange}
                />
              </div>
              <div className={BLOCK_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {t('Agent: Settings: Extra parameters: Reasoning: Summary: Title')}
                </IbTypography.Paragraph>
                <IbSelect
                  options={[
                    { value: AgentReasoningSummary.Auto, label: AgentReasoningSummary.Auto.toLowerCase() },
                    { value: AgentReasoningSummary.Concise, label: AgentReasoningSummary.Concise.toLowerCase() },
                    { value: AgentReasoningSummary.Detailed, label: AgentReasoningSummary.Detailed.toLowerCase() },
                  ]}
                  value={settings.extra.reasoning.summary}
                  onChange={onExtraReasoningSummaryChange}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  const sections = [];
  if (!interfaceType && agentSettings) {
    sections.push({ id: 'interface', title: t('Agent: Settings: Interface: Title'), content: renderInterfaceBlock() });
  }
  sections.push({
    id: 'runtime',
    title: t('Agent: Settings: Response generation: Title'),
    content: renderResponseGenerationBlock(),
  });
  if (interfaceType === DataInterfaceType.Api || settings.interface.type === DataInterfaceType.Api) {
    sections.push({
      id: 'interchange',
      title: t('Agent: Settings: Interchange: Title'),
      content: renderInterchangeBlock(),
    });
  }
  if (interfaceType === DataInterfaceType.Chat || settings.interface.type === DataInterfaceType.Chat) {
    sections.push({
      id: 'chat-history',
      title: t('Agent: Settings: Chat history: Title'),
      content: renderChatHistoryBlock(),
    });
  }
  sections.push({
    id: 'extra',
    title: t('Agent: Settings: Extra parameters: Title'),
    content: renderExtraParametersBlock(),
  });
  sections.push({
    id: 'ocr',
    title: t('Agent: Settings: OCR: Title'),
    content: renderOcrBlock(),
  });
  sections.push({
    id: 'asr',
    title: t('Agent: Settings: ASR: Title'),
    content: renderAsrBlock(),
  });

  const renderSchemaMonacoEditor = () => {
    if (!USE_SCHEMA_MONACO_EDITOR) {
      return null;
    }
    return (
      <SchemaEditorExpandable
        codeFolder={getCodeFolder(agentId)}
        options={MONACO_FULL_EDITOR_OPTIONS}
        status={schemaEditorStatus}
        value={schemaModalContent}
        onChange={onMonacoEditorChange}
        onValidate={onMonacoEditorValidate}
      />
    );
  };

  const renderSchemaJsonBuilder = () => {
    if (!USE_SCHEMA_JSON_BUILDER) {
      return null;
    }
    return (
      <div className={`${SCHEMA_FRAME_CLASS_NAME} ${schemaBuilderLoading ? SCHEMA_FRAME_LOADING_CLASS_NAME : ''}`}>
        <iframe
          ref={schemaBuilderRef}
          src={`/frames/jsb/index.html?lang=${i18n.resolvedLanguage}&full=on`}
          title="JSON schema builder"
          onLoad={onSchemaBuilderLoaded}
        />
        {schemaBuilderLoading && (
          <div className={SCHEMA_FRAME_SPIN_CLASS_NAME}>
            <IbSpin size="large" />
            <IbTypography.Paragraph>{t('Agent: Settings: Interchange: Schema: Loading')}</IbTypography.Paragraph>
          </div>
        )}
      </div>
    );
  };
  const renderSchemaControl = () => {
    if (USE_SCHEMA_MONACO_EDITOR && USE_SCHEMA_JSON_BUILDER) {
      return (
        <IbTabs activeKey={schemaControlTabKey} onChange={onSchemaControlTabChange}>
          <IbTabPane key={SchemeControlTabKey.MonacoEditor} tab="Monaco" tabKey={SchemeControlTabKey.MonacoEditor}>
            {renderSchemaMonacoEditor()}
          </IbTabPane>
          <IbTabPane key={SchemeControlTabKey.JsonBuilder} tab="JSON" tabKey={SchemeControlTabKey.JsonBuilder}>
            {renderSchemaJsonBuilder()}
          </IbTabPane>
        </IbTabs>
      );
    }
    if (USE_SCHEMA_MONACO_EDITOR) {
      return renderSchemaMonacoEditor();
    }
    if (USE_SCHEMA_JSON_BUILDER) {
      return renderSchemaJsonBuilder();
    }
    return null;
  };

  const renderSchemaModal = () => {
    const hasSpaceErrors = schemaModalSpaceNames.length > 0;
    return (
      <IbModal
        className={SCHEMA_MODAL_CLASS_NAME}
        footer={
          <>
            <IbButton disabled={hasSpaceErrors} onClick={onSchemaModalSave}>
              {t('Save')}
            </IbButton>
            <IbButton type="secondary" onClick={onSchemaModalHide}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        position={IbModalPosition.FullHeight}
        title={t('Agent: Settings: Interchange: Schema: Modal: Title')}
        visible={schemaModalVisible}
        width={1200}
        onCancel={onSchemaModalHide}
      >
        {renderSchemaControl()}
      </IbModal>
    );
  };

  const renderSaveButton = () => {
    const invalid =
      (settings.interface.input.data_format === DataInterchangeFormat.Schema &&
        (inputSchemaStatus !== CodeEditorStatus.Success || hasSchemaPropertyNamesWithSpaces(inputSchemaContent))) ||
      (settings.interface.output.data_format === DataInterchangeFormat.Schema &&
        (outputSchemaStatus !== CodeEditorStatus.Success || hasSchemaPropertyNamesWithSpaces(outputSchemaContent)));
    const button = (
      <IbButton disabled={saving || invalid} onClick={onSaveButtonClick}>
        {t('Agent: Settings: Save: Title')}
      </IbButton>
    );
    if (!invalid) {
      return button;
    }
    return <IbTooltip title={t('Agent: Settings: Save: Invalid')}>{button}</IbTooltip>;
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav sections={sections} />
      </div>
      <div className={FOOTER_CLASS_NAME}>{renderSaveButton()}</div>
      {renderSchemaModal()}
    </div>
  );
};

export default AgentGeneral;
